import { useEffect, useMemo, useRef, useState } from 'react';

import { Download, ZoomIn, ZoomOut, RotateCw, RotateCcw } from 'lucide-react';

import Loader from './Loader';
import Modal from './Modal';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { isMobileOrPWA, downloadFileFromUrl } from '../../utils/downloadUtils';

interface FileViewerProps {
  fileName: string;
  sasUrl: string;
  onClose: () => void;
  sidePanel?: React.ReactNode;
}


const getFileType = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (!ext) {
    return 'unknown';
  }

  // Check if file should auto-download (extensions stored with dots in constants)
  const extWithDot = `.${ext}`;
  if (FileTypeConstants.AUTO_DOWNLOAD.includes(extWithDot)) {
    return 'office';
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext)) {
    return 'image';
  }
  if (['pdf'].includes(ext)) {
    return 'pdf';
  }
  if (['mp4', 'webm', 'ogg'].includes(ext)) {
    return 'video';
  }
  if (['mp3', 'wav'].includes(ext)) {
    return 'audio';
  }
  if (['txt', 'csv', 'log'].includes(ext)) {
    return 'text';
  }

  return 'unknown';
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export const FileViewer: React.FC<FileViewerProps> = ({
  fileName,
  sasUrl,
  onClose,
  sidePanel,
}) => {
  const type = useMemo(() => getFileType(fileName), [fileName]);
  const [loading, setLoading] = useState(type !== 'unknown');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [baseSize, setBaseSize] = useState<{ width: number; height: number } | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Capture the natural on-screen size the image is fitted to (before any zoom/rotate),
  // so the spacer below can be given a real box the same size as the transformed image.
  const handleImageLoad = (): void => {
    setLoading(false);
    const img = imageRef.current;
    if (img) {
      setBaseSize({ width: img.clientWidth, height: img.clientHeight });
    }
  };

  // Re-centre the scroll on the middle of the image after every zoom / rotate. Because
  // the spacer is sized to the transformed image, overflow is symmetric and every edge
  // stays reachable at any rotation — we just reset the resting position to the centre.
  useEffect(() => {
    const container = imageContainerRef.current;
    if (container) {
      container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
      container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;
    }
  }, [zoom, rotation, baseSize]);

  const handleZoomIn = (): void => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const handleZoomOut = (): void => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  const handleRotateCw = (): void => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleRotateCcw = (): void => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  // Auto-download for mobile/PWA, office files, or unknown file types
  useEffect(() => {
    const shouldAutoDownload = isMobileOrPWA() || type === 'office' || type === 'unknown';

    if (shouldAutoDownload) {
      downloadFileFromUrl(
        sasUrl,
        fileName,
        () => onClose(),
        (error) => {
          console.error('Auto-download failed:', error);
          onClose();
        }
      );
    }
  }, [type, sasUrl, fileName, onClose]);


  return (
    <Modal
      isOpen
      onClose={onClose}
      title={fileName}
      widthClassName={sidePanel ? 'max-w-5xl' : 'max-w-2xl'}
      actions={
        <>
          {type === 'image' && (
            <>
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm cursor-pointer
                font-medium tracking-wide transition duration-300 rounded-full justify-self-center whitespace-nowrap
                text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm cursor-pointer
                font-medium tracking-wide transition duration-300 rounded-full justify-self-center whitespace-nowrap
                text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleRotateCcw}
                className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm cursor-pointer
                font-medium tracking-wide transition duration-300 rounded-full justify-self-center whitespace-nowrap
                text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none"
                aria-label="Rotate counter-clockwise"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleRotateCw}
                className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm cursor-pointer
                font-medium tracking-wide transition duration-300 rounded-full justify-self-center whitespace-nowrap
                text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none"
                aria-label="Rotate clockwise"
              >
                <RotateCw className="w-5 h-5" />
              </button>
            </>
          )}
          <a
            href={sasUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm cursor-pointer
            font-medium tracking-wide transition duration-300 rounded-full justify-self-center whitespace-nowrap
            text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none z-100"
            aria-label="Download file"
          >
            <Download className="w-5 h-5" />
          </a>
        </>
      }
    >
      <div className={sidePanel ? 'flex flex-col-reverse md:flex-row gap-4 min-h-[200px]' : undefined}>
        {sidePanel && (
          <div className="w-full md:w-72 shrink-0 md:border-r md:pr-4 overflow-y-auto max-h-[75vh]">
            {sidePanel}
          </div>
        )}

        <div className="relative flex-1 min-w-0 space-y-4 min-h-[200px]">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
              <Loader />
              <p className="mt-2 text-sm text-gray-500">Loading file...</p>
            </div>
          )}

          {type === 'image' && (
            <div
              ref={imageContainerRef}
              className="grid overflow-auto"
              style={{ maxHeight: '75vh', placeItems: 'safe center' }}
            >
              {/* Spacer sized to the transformed image so the scroll area matches what's
                visible; the image is centred inside it and transformed about its centre. */}
              <div
                className="relative"
                style={
                  baseSize
                    ? {
                      width: (rotation % 180 === 0 ? baseSize.width : baseSize.height) * zoom,
                      height: (rotation % 180 === 0 ? baseSize.height : baseSize.width) * zoom,
                    }
                    : undefined
                }
              >
                <img
                  ref={imageRef}
                  src={sasUrl}
                  alt={fileName}
                  onLoad={handleImageLoad}
                  className="block"
                  style={
                    baseSize
                      ? {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: baseSize.width,
                        height: baseSize.height,
                        transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${zoom})`,
                      }
                      : { maxHeight: '75vh', maxWidth: '100%' }
                  }
                />
              </div>
            </div>
          )}

          {type === 'pdf' && (
            <iframe
              src={sasUrl}
              title={fileName}
              onLoad={() => setLoading(false)}
              className="w-full border-0"
              style={{ height: '75vh' }}
            />
          )}

          {type === 'video' && (
            <video
              controls
              className="max-w-full max-h-full mx-auto"
              onLoadedData={() => setLoading(false)}
            >
              <source src={sasUrl} />
              Your browser does not support the video tag.
            </video>
          )}

          {type === 'audio' && (
            <audio
              controls
              className="w-full"
              onLoadedData={() => setLoading(false)}
            >
              <source src={sasUrl} />
              Your browser does not support the audio element.
            </audio>
          )}

          {type === 'text' && (
            <iframe
              src={sasUrl}
              title={fileName}
              onLoad={() => setLoading(false)}
              className="w-full h-full border"
            />
          )}

          {type === 'unknown' && (
            <div className="text-center text-gray-500">
              Preview not available. Use the download link above.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
